// =============================================================================
// staticSite/assetFetchClient — klient Edge Function `fetch-asset`.
//
// Po co: obrazy z /wp-content/uploads/* i sitemap WP nie maja naglowkow CORS,
// wiec przegladarka nie odczyta bajtow. Edge Function (JWT sesji + allowed_users
// + allowlist hostow per website) pobiera je server-side i oddaje z CORS.
// Brak wdrozonej funkcji = graceful: analiza dziala bez sitemap, deploy bez obrazow.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { env } from "@/lib/env";

export class AssetFetchError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
  }
}

async function callFetchAsset(url: string, websiteId: string, mode: "bytes" | "text"): Promise<Response> {
  const base = env("VITE_SUPABASE_URL");
  const anon = env("VITE_SUPABASE_ANON_KEY");
  if (!base || !anon) throw new AssetFetchError("Brak VITE_SUPABASE_URL / ANON_KEY");
  const { data } = await supabase.auth.getSession();
  const jwt = data.session?.access_token;
  if (!jwt) throw new AssetFetchError("Brak sesji — zaloguj sie ponownie", 401);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 30_000);
  try {
    const res = await fetch(`${base.replace(/\/$/, "")}/functions/v1/fetch-asset`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
        apikey: anon,
      },
      body: JSON.stringify({ url, websiteId, mode }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.error) msg = `${msg}: ${j.error}`;
      } catch {
        // ignore
      }
      throw new AssetFetchError(msg, res.status);
    }
    return res;
  } catch (e) {
    if (e instanceof AssetFetchError) throw e;
    if ((e as Error)?.name === "AbortError") throw new AssetFetchError("Timeout 30 s");
    throw new AssetFetchError(`fetch-asset niedostepne: ${(e as Error)?.message || e}`);
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchAssetBytes(url: string, websiteId: string): Promise<{ bytes: Uint8Array; contentType: string }> {
  const res = await callFetchAsset(url, websiteId, "bytes");
  const buf = new Uint8Array(await res.arrayBuffer());
  return { bytes: buf, contentType: (res.headers.get("content-type") || "application/octet-stream").split(";")[0].trim() };
}

export async function fetchAssetText(url: string, websiteId: string): Promise<string> {
  const res = await callFetchAsset(url, websiteId, "text");
  return res.text();
}

/** Fetcher dla resolveAssets (assetCollector). */
export function makeAssetFetcher(websiteId: string) {
  return (url: string) => fetchAssetBytes(url, websiteId);
}
